/**
 * ZinsserAnatomyPanel - Sentence-per-line structural analysis view
 *
 * Displays each sentence on its own line with word-level grammatical coloring.
 * Haiku parses the structure server-side for accuracy. Before the parse returns,
 * plain uncolored text is shown (client-side sentence splitting as placeholder).
 *
 * Once Haiku responds, the panel renders Haiku's sentences directly — no matching
 * needed. The Haiku parse IS the display.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useZinsserStore } from '@/stores/useZinsserStore'
import type {
  SentenceStructure as AnatomySentenceStructure,
  ParagraphStructure as AnatomyParagraphStructure,
  GrammaticalRole,
} from '@/lib/anatomy-parser'
import {
  ALL_ROLES,
  DEFAULT_ACTIVE_ROLES,
  ROLE_COLORS,
  ROLE_LABELS,
} from '@/lib/anatomy-parser'

// ──────────────────────────────────────────────
// Client-side sentence splitting (placeholder before Haiku responds)
// ──────────────────────────────────────────────

const ABBREVIATIONS = new Set([
  'mr', 'mrs', 'ms', 'dr', 'prof', 'sr', 'jr', 'st', 'ave', 'blvd',
  'gen', 'gov', 'sgt', 'cpl', 'pvt', 'capt', 'lt', 'col', 'maj',
  'dept', 'univ', 'assn', 'bros', 'inc', 'ltd', 'co', 'corp',
  'vs', 'etc', 'approx', 'appt', 'est', 'min', 'max',
  'jan', 'feb', 'mar', 'apr', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec',
  'u.s', 'u.k', 'e.g', 'i.e', 'a.m', 'p.m',
])

function segmentSentences(text: string): string[] {
  if (!text.trim()) return []
  const sentences: string[] = []
  let current = ''
  for (let i = 0; i < text.length; i++) {
    current += text[i]
    if (text[i] === '.' || text[i] === '!' || text[i] === '?') {
      const rest = text.slice(i + 1)
      const match = rest.match(/^(\s+)([A-Z])/)
      if (match) {
        const wordBefore = current.trimEnd().split(/\s+/).pop()?.toLowerCase().replace(/\.$/, '') || ''
        if (ABBREVIATIONS.has(wordBefore)) continue
        sentences.push(current.trim())
        current = ''
        i += match[1].length
      }
    }
  }
  if (current.trim()) sentences.push(current.trim())
  return sentences
}

function splitIntoParagraphGroups(content: string): string[][] {
  return content.split(/\n\s*\n/)
    .map(p => p.trim())
    .filter(p => p.length > 0)
    .map(p => {
      const lines = p.split('\n').map(l => l.trim()).filter(l => l.length > 0)
      if (lines.length > 1) return lines
      return segmentSentences(p)
    })
}

// ──────────────────────────────────────────────
// Role color class helper
// ──────────────────────────────────────────────

function getRoleClass(role: string, activeRoles: Set<GrammaticalRole>): string {
  if (activeRoles.has(role as GrammaticalRole)) return `anatomy-${role}`
  return 'anatomy-dimmed'
}

// ──────────────────────────────────────────────
// Sparkline component
// ──────────────────────────────────────────────

function RhythmSparkline({ rhythm }: { rhythm: number[] }) {
  if (rhythm.length === 0) return null
  const max = Math.max(...rhythm, 1)
  const barWidth = 4
  const gap = 2
  const height = 16
  const width = rhythm.length * (barWidth + gap) - gap
  return (
    <svg width={width} height={height} className="inline-block align-middle ml-1.5 opacity-60">
      {rhythm.map((wc, i) => {
        const barHeight = Math.max(2, (wc / max) * height)
        return (
          <rect key={i} x={i * (barWidth + gap)} y={height - barHeight}
            width={barWidth} height={barHeight} rx={1} className="fill-muted-foreground/50" />
        )
      })}
    </svg>
  )
}

// ──────────────────────────────────────────────
// Colored sentence renderer
// ──────────────────────────────────────────────

function ColoredSentence({
  structure,
  activeRoles,
}: {
  structure: AnatomySentenceStructure
  activeRoles: Set<GrammaticalRole>
}) {
  const renderText = structure.text
  const spans = structure.spans
  const elements: React.ReactNode[] = []
  let lastEnd = 0

  for (let i = 0; i < spans.length; i++) {
    const span = spans[i]
    const start = Math.min(span.start, renderText.length)
    const end = Math.min(span.end, renderText.length)
    if (start >= end) continue

    if (start > lastEnd) {
      elements.push(
        <span key={`gap-${i}`} className="anatomy-uncolored">
          {renderText.slice(lastEnd, start)}
        </span>
      )
    }

    const roleClass = getRoleClass(span.role, activeRoles)
    elements.push(
      <span key={`span-${i}`}
        className={`${roleClass} cursor-default transition-colors duration-150`}
        title={`${span.role.toUpperCase()}${span.detail ? ` (${span.detail})` : ''}`}
      >
        {renderText.slice(start, end)}
      </span>
    )
    lastEnd = end
  }

  if (lastEnd < renderText.length) {
    elements.push(
      <span key="trailing" className="anatomy-uncolored">{renderText.slice(lastEnd)}</span>
    )
  }

  return <>{elements}</>
}

// ──────────────────────────────────────────────
// Sentence line component
// ──────────────────────────────────────────────

function SentenceLine({
  sentence,
  structure,
  activeRoles,
  onInsightClick,
  onEdit,
}: {
  sentence: string
  structure?: AnatomySentenceStructure
  activeRoles: Set<GrammaticalRole>
  onInsightClick: () => void
  onEdit?: (newText: string) => void
}) {
  const [editing, setEditing] = useState(false)
  const [editText, setEditText] = useState(sentence)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => { setEditText(sentence) }, [sentence])

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.selectionStart = inputRef.current.value.length
      inputRef.current.selectionEnd = inputRef.current.value.length
      inputRef.current.style.height = 'auto'
      inputRef.current.style.height = inputRef.current.scrollHeight + 'px'
    }
  }, [editing])

  const handleBlur = useCallback(() => {
    setEditing(false)
    if (editText.trim() !== sentence.trim() && onEdit) {
      onEdit(editText)
    }
  }, [editText, sentence, onEdit])

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      ;(e.target as HTMLTextAreaElement).blur()
    }
    if (e.key === 'Escape') {
      setEditText(sentence)
      setEditing(false)
    }
  }, [sentence])

  const wordCount = structure?.word_count ?? sentence.split(/\s+/).filter(Boolean).length
  const openerAbbrev = structure?.opener_type
    ? structure.opener_type.charAt(0).toUpperCase()
    : ''

  return (
    <div className="group flex items-start gap-2 py-0.5 hover:bg-muted/20 rounded transition-colors">
      <button
        onClick={onInsightClick}
        className="flex-shrink-0 mt-[5px] w-2 h-2 rounded-full bg-muted-foreground/20 group-hover:bg-muted-foreground/40 hover:bg-amber-400/60 cursor-pointer transition-colors"
        title="Analyze this sentence"
      />
      <div className="flex-1 min-w-0">
        {editing ? (
          <textarea
            ref={inputRef}
            value={editText}
            onChange={e => {
              setEditText(e.target.value)
              e.target.style.height = 'auto'
              e.target.style.height = e.target.scrollHeight + 'px'
            }}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            className="w-full bg-transparent border-none outline-none resize-none font-[inherit] text-[inherit] leading-[inherit] p-0"
            rows={1}
          />
        ) : (
          <span className="cursor-text" onDoubleClick={() => setEditing(true)}>
            {structure ? (
              <ColoredSentence structure={structure} activeRoles={activeRoles} />
            ) : (
              sentence
            )}
          </span>
        )}
      </div>
      <div className="flex-shrink-0 flex items-center gap-1.5 text-[10px] text-muted-foreground/50 font-mono tabular-nums opacity-0 group-hover:opacity-100 transition-opacity mt-[3px]">
        <span>{wordCount}w</span>
        {openerAbbrev && <span className="text-muted-foreground/30">{openerAbbrev}</span>}
      </div>
    </div>
  )
}

// ──────────────────────────────────────────────
// Paragraph group component (from Haiku parse)
// ──────────────────────────────────────────────

function ParsedParagraphGroup({
  paragraph,
  paraIndex,
  activeRoles,
  onParagraphInsightClick,
  onSentenceInsightClick,
  onEditSentence,
}: {
  paragraph: AnatomyParagraphStructure
  paraIndex: number
  activeRoles: Set<GrammaticalRole>
  onParagraphInsightClick: (paragraphText: string) => void
  onSentenceInsightClick: (sentenceText: string, paragraphText: string) => void
  onEditSentence: (paraIndex: number, sentIndex: number, newText: string) => void
}) {
  const paragraphText = paragraph.sentences.map(s => s.text).join(' ')

  return (
    <div className="mb-5">
      {paragraph.sentences.map((sent, i) => (
        <SentenceLine
          key={i}
          sentence={sent.text}
          structure={sent}
          activeRoles={activeRoles}
          onInsightClick={() => onSentenceInsightClick(sent.text, paragraphText)}
          onEdit={(newText) => onEditSentence(paraIndex, i, newText)}
        />
      ))}
      {paragraph.sentences.length > 1 && (
        <div className="flex items-center gap-2 mt-1.5 pl-5">
          <button
            onClick={() => onParagraphInsightClick(paragraphText)}
            className="text-muted-foreground/30 text-xs cursor-pointer hover:text-amber-500/70 transition-colors"
            title="Analyze this paragraph"
          >
            ◆
          </button>
          <span className="text-[10px] text-muted-foreground/40 font-mono tabular-nums">
            {paragraph.rhythm.join(' · ')}
          </span>
          <RhythmSparkline rhythm={paragraph.rhythm} />
          {paragraph.opener_pattern.length > 0 && (
            <span className="text-[10px] text-muted-foreground/30 font-mono">
              {paragraph.opener_pattern.map(o => o.charAt(0).toUpperCase()).join(' ')}
            </span>
          )}
        </div>
      )}
    </div>
  )
}

// ──────────────────────────────────────────────
// Placeholder paragraph group (before Haiku responds)
// ──────────────────────────────────────────────

function PlaceholderParagraphGroup({
  sentences,
  onSentenceInsightClick,
}: {
  sentences: string[]
  onSentenceInsightClick: (sentenceText: string, paragraphText: string) => void
}) {
  const paragraphText = sentences.join(' ')
  const activeRoles = new Set<GrammaticalRole>()

  return (
    <div className="mb-5 animate-pulse">
      {sentences.map((sentence, i) => (
        <SentenceLine
          key={i}
          sentence={sentence}
          activeRoles={activeRoles}
          onInsightClick={() => onSentenceInsightClick(sentence, paragraphText)}
        />
      ))}
    </div>
  )
}

// ──────────────────────────────────────────────
// Main Anatomy Panel
// ──────────────────────────────────────────────

export function ZinsserAnatomyPanel() {
  const {
    brainstormContent,
    brainstormDisplayContent,
    brainstormCleanContent,
    updateBrainstormContent,
    anatomyStructure,
    anatomyInsightLoading,
    anatomyStructureLoading,
    fetchAnatomyStructure,
    patchAnatomySentence,
    reparseAnatomyParagraph,
    addAnatomyInsightToChat,
  } = useZinsserStore()

  const displayContent = brainstormCleanContent || brainstormDisplayContent || brainstormContent
  const parseDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastParsedContentRef = useRef<string>('')

  // Toggleable role highlighting
  const [activeRoles, setActiveRoles] = useState<Set<GrammaticalRole>>(
    () => new Set(DEFAULT_ACTIVE_ROLES)
  )

  const toggleRole = useCallback((role: GrammaticalRole) => {
    setActiveRoles(prev => {
      const next = new Set(prev)
      if (next.has(role)) next.delete(role)
      else next.add(role)
      return next
    })
  }, [])

  // Client-side sentence split (placeholder while Haiku loads)
  const placeholderGroups = useMemo(
    () => splitIntoParagraphGroups(displayContent),
    [displayContent]
  )

  // Fetch structural analysis on debounce
  useEffect(() => {
    if (!displayContent.trim()) return
    if (displayContent === lastParsedContentRef.current) return

    if (parseDebounceRef.current) clearTimeout(parseDebounceRef.current)

    parseDebounceRef.current = setTimeout(() => {
      lastParsedContentRef.current = displayContent
      fetchAnatomyStructure(displayContent)
    }, 800)

    return () => {
      if (parseDebounceRef.current) clearTimeout(parseDebounceRef.current)
    }
  }, [displayContent, fetchAnatomyStructure])

  // Insight click handlers
  const handleSentenceInsight = useCallback(
    (sentenceText: string, paragraphText: string) => {
      if (anatomyInsightLoading) return
      addAnatomyInsightToChat(sentenceText, 'sentence', paragraphText)
    },
    [addAnatomyInsightToChat, anatomyInsightLoading]
  )

  const handleParagraphInsight = useCallback(
    (paragraphText: string) => {
      if (anatomyInsightLoading) return
      addAnatomyInsightToChat(paragraphText, 'paragraph')
    },
    [addAnatomyInsightToChat, anatomyInsightLoading]
  )

  // Ensure each sentence ends with terminal punctuation before joining
  const ensureTerminalPunctuation = (text: string): string => {
    const trimmed = text.trim()
    if (!trimmed) return trimmed
    const lastChar = trimmed[trimmed.length - 1]
    if (lastChar === '.' || lastChar === '!' || lastChar === '?') return trimmed
    return trimmed + '.'
  }

  // Handle sentence edit:
  // 1. Instantly show the new text (uncolored)
  // 2. Sync to canvas
  // 3. Re-parse just that paragraph with Haiku (colors arrive ~1s later)
  const handleEditSentence = useCallback(
    (paraIndex: number, sentIndex: number, newText: string) => {
      if (!anatomyStructure) return

      // 1. Patch local structure immediately — shows edit with no colors
      patchAnatomySentence(paraIndex, sentIndex, newText)

      // 2. Sync full document to canvas
      const freshStructure = useZinsserStore.getState().anatomyStructure
      if (freshStructure) {
        const newDoc = freshStructure.paragraphs.map(para =>
          para.sentences.map(s => ensureTerminalPunctuation(s.text)).join(' ')
        ).join('\n\n')
        updateBrainstormContent(newDoc)
      }

      // 3. Re-parse only the changed paragraph
      reparseAnatomyParagraph(paraIndex)
    },
    [anatomyStructure, patchAnatomySentence, reparseAnatomyParagraph, updateBrainstormContent]
  )

  const hasParsedData = anatomyStructure && anatomyStructure.paragraphs.length > 0

  if (!displayContent.trim()) {
    return (
      <div className="h-full flex items-center justify-center p-8">
        <p className="text-sm text-muted-foreground italic">
          Write something on the Canvas tab to see its anatomy here.
        </p>
      </div>
    )
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="px-6 py-5 max-w-3xl mx-auto prose-zinsser">
        {/* Toggleable color legend */}
        <div className="flex flex-wrap items-center gap-1.5 mb-5 pb-3 border-b border-border">
          {ALL_ROLES.map(role => {
            const isActive = activeRoles.has(role)
            return (
              <button
                key={role}
                onClick={() => toggleRole(role)}
                className={`px-2 py-0.5 rounded text-[10px] font-mono tracking-wide transition-all duration-150 ${
                  isActive ? 'opacity-100' : 'opacity-30 hover:opacity-50'
                }`}
                style={{ color: ROLE_COLORS[role] }}
                title={`${isActive ? 'Hide' : 'Show'} ${role}s`}
              >
                {ROLE_LABELS[role].toUpperCase()}
              </button>
            )
          })}
          {anatomyStructureLoading && (
            <span className="ml-auto text-[10px] text-muted-foreground/40 italic animate-pulse">
              parsing...
            </span>
          )}
        </div>

        {/* Render Haiku-parsed paragraphs when available, placeholder otherwise */}
        {hasParsedData
          ? anatomyStructure!.paragraphs.map((para, i) => (
              <ParsedParagraphGroup
                key={`parsed-${i}`}
                paragraph={para}
                paraIndex={i}
                activeRoles={activeRoles}
                onParagraphInsightClick={handleParagraphInsight}
                onSentenceInsightClick={handleSentenceInsight}
                onEditSentence={handleEditSentence}
              />
            ))
          : placeholderGroups.map((sentences, i) => (
              <PlaceholderParagraphGroup
                key={`placeholder-${i}`}
                sentences={sentences}
                onSentenceInsightClick={handleSentenceInsight}
              />
            ))
        }

        {anatomyInsightLoading && (
          <div className="fixed bottom-20 left-1/2 -translate-x-1/2 z-50 px-3 py-1.5 bg-amber-500/10 border border-amber-500/20 rounded-full text-xs text-amber-600 animate-pulse">
            Analyzing...
          </div>
        )}
      </div>
    </div>
  )
}
